import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { FileText, Download, X, Maximize2, Minimize2, ExternalLink, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Returns the preview URL for a given upload_id.
 * Uses the authenticated /api/files/{id}/preview endpoint.
 */
export function getPreviewUrl(uploadId) {
  return `${API_URL}/api/files/${uploadId}/preview`;
}

/**
 * FilePreviewModal — reusable document viewer modal.
 * Renders PDFs in an iframe and images inline.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - uploadId: string (file_uploads.upload_id)
 *  - filename: string (original_filename for display)
 *  - token: string (auth token for authenticated preview)
 */
export function FilePreviewModal({ open, onClose, uploadId, filename, token }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  if (!open || !uploadId) return null;

  const ext = (filename || '').split('.').pop().toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  const isPdf = ext === 'pdf';
  const previewUrl = `${getPreviewUrl(uploadId)}?token=${encodeURIComponent(token)}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setLoadError(false); onClose(); } }}>
      <DialogContent className={`p-0 gap-0 overflow-hidden ${fullscreen ? 'max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh]' : 'max-w-4xl max-h-[85vh]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center space-x-2 min-w-0">
            <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <span className="text-sm font-medium text-slate-900 truncate" data-testid="preview-filename">{filename}</span>
            <span className="text-[10px] text-slate-400 uppercase font-mono">{ext}</span>
          </div>
          <div className="flex items-center space-x-1">
            {!loadError && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => window.open(previewUrl, '_blank')}
                  title="Open in new tab"
                  data-testid="preview-open-tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => setFullscreen(f => !f)}
                  title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  data-testid="preview-fullscreen"
                >
                  {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                </Button>
                <a
                  href={previewUrl}
                  download={filename}
                  className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                  title="Download"
                  data-testid="preview-download"
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className={`bg-slate-100 flex items-center justify-center overflow-auto ${fullscreen ? 'h-[calc(95vh-52px)]' : 'h-[70vh]'}`} data-testid="preview-content">
          {loadError ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-200" strokeWidth={1} />
              <p className="text-sm font-medium text-slate-500 mb-1">Preview Unavailable</p>
              <p className="text-xs text-slate-400 max-w-xs mx-auto">This file was uploaded before cloud storage was configured. Please re-upload the document to enable preview.</p>
            </div>
          ) : isPdf ? (
            <iframe
              src={previewUrl}
              className="w-full h-full border-0"
              title={`Preview: ${filename}`}
              data-testid="preview-pdf-iframe"
              onError={() => setLoadError(true)}
              onLoad={(e) => {
                // Check if iframe loaded an error response
                try {
                  const doc = e.target.contentDocument;
                  if (doc && doc.body) {
                    const text = doc.body.innerText || '';
                    if (text.includes('File not available') || text.includes('"detail"')) {
                      setLoadError(true);
                    }
                  }
                } catch (e) { console.debug('FilePreviewModal cross-origin content check skipped', e); }
              }}
            />
          ) : isImage ? (
            <img
              src={previewUrl}
              alt={filename}
              className="max-w-full max-h-full object-contain"
              data-testid="preview-image"
              onError={() => setLoadError(true)}
            />
          ) : (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-200" strokeWidth={1} />
              <p className="text-sm font-medium text-slate-500 mb-1">{filename}</p>
              <p className="text-xs text-slate-400 mb-4">Preview not available for this file type</p>
              <a
                href={previewUrl}
                download={filename}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" /> Download File
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PreviewButton — small inline button to trigger file preview.
 * Place next to any file listing. Manages its own modal state.
 *
 * Props:
 *  - uploadId: string
 *  - filename: string
 *  - token: string
 *  - size: 'sm' | 'xs' (default: 'sm')
 *  - variant: 'outline' | 'ghost' (default: 'outline')
 */
export function PreviewButton({ uploadId, filename, token, size = 'sm', variant = 'outline', className = '', hasStorage = true }) {
  const [open, setOpen] = useState(false);

  if (!uploadId || !hasStorage) return null;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        className={`${size === 'xs' ? 'h-6 text-[10px] px-2' : 'h-7 text-xs'} ${className}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`preview-btn-${uploadId}`}
      >
        <Eye className={`${size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5'} mr-1`} /> Preview
      </Button>
      <FilePreviewModal
        open={open}
        onClose={() => setOpen(false)}
        uploadId={uploadId}
        filename={filename}
        token={token}
      />
    </>
  );
}
